import { tzOpts } from "@/lib/tz";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Area, Bar, BarChart, CartesianGrid, Cell, ComposedChart, Line, Pie, PieChart,
  RadialBar, RadialBarChart, ResponsiveContainer, Tooltip, XAxis, YAxis, PolarAngleAxis,
} from "recharts";
import {
  Activity, Users, MessageSquare, PlugZap, Play, Clock, Pause, Bot,
  AlertTriangle, TrendingDown, Wallet, ShieldAlert, CheckCircle2, XCircle,
  ArrowRight, HeartPulse, CalendarClock, Percent, Receipt, Sparkles, RefreshCw,
} from "lucide-react";
import {
  COLORS, Kpi, ChartCard, ChartTooltip, LegendChips, Empty, Skeleton,
  brl, brlCompact, num, dm, weekday, fmtHours,
} from "@/components/dash/parts";

const HEALTH_LABELS = {
  whatsapp: "WhatsApp",
  mkauth: "mk-auth",
  efi: "EFI",
  openpix: "OpenPix",
  scheduler: "Agendador",
};

const PERIODS = [
  { id: "7", label: "7 dias" }, { id: "30", label: "30 dias" }, { id: "90", label: "90 dias" }, { id: "month", label: "Mês atual" }, { id: "custom", label: "Personalizado" },
];
const MSG_COLORS = { cobranca: "#34d399", pedido: "#38bdf8", atendimento: "#a78bfa", marketing: "#fbbf24" };
const AGING_COLORS = ["#fbbf24", "#fb923c", "#f97316", "#ef4444", "#b91c1c"];
const axis = { fill: COLORS.text, fontSize: 11 };

export default function Dashboard() {
  const [metrics, setMetrics] = useState(null);
  const [an, setAn] = useState(null);
  const [days, setDays] = useState("30"); // "7" | "30" | "90" | "month" | "custom"
  const [draft, setDraft] = useState({ start: "", end: "" });
  const [range, setRange] = useState({ start: "", end: "" }); // intervalo personalizado aplicado
  const [loading, setLoading] = useState(false);
  const [anLoading, setAnLoading] = useState(false);
  const [hidden, setHidden] = useState({});

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/dashboard/metrics");
      setMetrics(data);
    } catch (ex) {
      toast.error(formatApiError(ex));
    }
  }, []);

  const loadAnalytics = useCallback(async (d, silent) => {
    if (d === "custom" && !(range.start && range.end)) return;
    if (!silent) setAnLoading(true);
    try {
      const params = d === "custom" ? { start: range.start, end: range.end }
        : d === "month" ? { period: "month" } : { days: Number(d) };
      const { data } = await api.get("/dashboard/analytics", { params });
      setAn(data);
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setAnLoading(false);
    }
  }, [range]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadAnalytics(days); }, [days, loadAnalytics]);
  useEffect(() => {
    const t = setInterval(() => { load(); loadAnalytics(days, true); }, 60000);
    return () => clearInterval(t);
  }, [days, load, loadAnalytics]);

  const runNow = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/scheduler/run-now");
      toast.success(`Rodada executada. Cobranças: ${data?.billing_sent ?? 0} • Aniversários: ${data?.birthday_sent ?? 0}`);
      load();
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setLoading(false);
    }
  };

  const togglePause = async () => {
    const next = !metrics?.bot_paused;
    try {
      await api.put("/tenant/settings", { bot_paused: next });
      toast.success(next ? "Bot pausado — mensagens recebidas não serão respondidas" : "Bot reativado");
      load();
    } catch (ex) {
      toast.error(formatApiError(ex));
    }
  };

  const reactivateMessaging = async () => {
    try {
      await api.post("/health/unpause");
      toast.success("Envios reativados");
      load();
    } catch (ex) {
      toast.error(formatApiError(ex));
    }
  };

  const healthChecks = metrics?.health?.checks || {};
  const brokenChecks = Object.entries(healthChecks).filter(([, v]) => v.configured && !v.ok);

  const k = an?.kpis;
  const plabel = days === "month" ? "mês" : days === "custom" ? `${an?.period_days || ""}d` : `${days}d`;
  const fmtD = (iso) => (iso ? iso.split("-").reverse().join("/") : "");
  const applyRange = () => {
    if (!draft.start || !draft.end) return toast.error("Informe a data inicial e a final.");
    if (draft.start > draft.end) return toast.error("A data inicial não pode ser depois da final.");
    setRange({ ...draft });
  };
  const daily = an?.daily || [];
  const toggle = (key) => setHidden((h) => ({ ...h, [key]: !h[key] }));

  // Destaques automaticos (estilo "insights")
  const insights = useMemo(() => {
    if (!an) return [];
    const out = [];
    const best = [...daily].sort((a, b) => b.total - a.total)[0];
    if (best && best.total > 0) out.push({ icon: Wallet, text: <>Melhor dia: <b>{dm(best.date)} ({weekday(best.date)})</b> com {brl(best.total)}</> });
    const peak = [...(an.hours || [])].sort((a, b) => b.count - a.count)[0];
    if (peak && peak.count > 0) out.push({ icon: Clock, text: <>Horário de pico dos pagamentos: <b>{String(peak.h).padStart(2, "0")}h</b></> });
    if (k?.ttp?.median_hours != null) out.push({ icon: CalendarClock, text: <>Metade dos clientes paga em até <b>{fmtHours(k.ttp.median_hours)}</b></> });
    const worst = [...(an.aging || [])].sort((a, b) => b.total - a.total)[0];
    if (worst && worst.total > 0) out.push({ icon: TrendingDown, text: <>Maior concentração de atraso: <b>{worst.label}</b> ({brl(worst.total)})</> });
    const fc = [...(an.forecast || [])].sort((a, b) => b.total - a.total)[0];
    if (fc && fc.total > 0) out.push({ icon: Receipt, text: <>Maior vencimento à frente: <b>{dm(fc.date)}</b> ({brl(fc.total)}, {fc.count} boletos)</> });
    return out;
  }, [an, daily, k]);

  const convRadial = k ? [{ name: "Conversão", value: k.conversion.rate, fill: COLORS.efi }] : [];
  const providerTotal = (an?.by_provider || []).reduce((s, p) => s + p.value, 0);
  const forecastData = (an?.forecast || []).map((f) => ({ ...f, label: dm(f.date), wd: weekday(f.date) }));

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between border-b border-slate-800 pb-5 flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-400">Overview</div>
          <h1 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white mt-1">Painel</h1>
          <p className="text-sm text-slate-500 mt-2 flex items-center gap-2 flex-wrap">
            Visão da operação de cobrança.
            {an?.generated_at && (
              <span className="text-xs text-slate-600">
                Atualizado às {new Date(an.generated_at).toLocaleTimeString("pt-BR", { ...tzOpts(), hour: "2-digit", minute: "2-digit" })}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex rounded-lg border border-slate-800 bg-slate-900/60 p-0.5" data-testid="period-selector">
            {PERIODS.map((p) => (
              <button
                key={p.id}
                onClick={() => setDays(p.id)}
                className={`px-3 py-1.5 text-xs rounded-md transition whitespace-nowrap ${days === p.id ? "bg-emerald-500 text-emerald-950 font-semibold" : "text-slate-400 hover:text-white"}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          {days === "custom" && (
            <div className="flex items-center gap-1.5" data-testid="custom-range">
              <input type="date" value={draft.start} max={draft.end || undefined}
                onChange={(e) => setDraft((d) => ({ ...d, start: e.target.value }))}
                className="h-8 rounded-md border border-slate-800 bg-slate-900/60 px-2 text-xs text-slate-200" />
              <span className="text-xs text-slate-500">até</span>
              <input type="date" value={draft.end} min={draft.start || undefined}
                onChange={(e) => setDraft((d) => ({ ...d, end: e.target.value }))}
                className="h-8 rounded-md border border-slate-800 bg-slate-900/60 px-2 text-xs text-slate-200" />
              <Button size="sm" onClick={applyRange} className="h-8 bg-emerald-500 text-emerald-950 hover:bg-emerald-400">Aplicar</Button>
            </div>
          )}
          <Button variant="ghost" size="sm" onClick={() => { load(); loadAnalytics(days); }} className="text-slate-400 hover:text-white" title="Atualizar">
            <RefreshCw className={`w-4 h-4 ${anLoading ? "animate-spin" : ""}`} />
          </Button>
          <Button
            data-testid="bot-pause-toggle-btn"
            onClick={togglePause}
            variant="outline"
            className={metrics?.bot_paused
              ? "border-amber-500/50 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20 hover:text-amber-100"
              : "border-slate-700 hover:bg-slate-800 hover:text-white"}
          >
            {metrics?.bot_paused ? <Bot className="w-4 h-4 mr-2" /> : <Pause className="w-4 h-4 mr-2" />}
            {metrics?.bot_paused ? "Reativar Bot" : "Pausar Bot"}
          </Button>
          <Button
            data-testid="run-now-btn"
            onClick={runNow}
            disabled={loading}
            className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold"
          >
            <Play className="w-4 h-4 mr-2" strokeWidth={2} />
            Rodar Cobrança Agora
          </Button>
        </div>
      </header>

      {/* ---- Avisos urgentes (só aparecem quando fazem sentido) ---- */}
      {metrics?.messaging_paused?.active && (
        <div
          data-testid="messaging-paused-banner"
          className="flex items-center justify-between gap-4 rounded-lg border border-red-500/30 bg-red-500/10 p-4 flex-wrap"
        >
          <div className="flex items-center gap-3">
            <ShieldAlert className="w-5 h-5 text-red-400 shrink-0" />
            <div>
              <div className="text-sm font-medium text-red-200">Envios pausados por segurança anti-bloqueio</div>
              {metrics.messaging_paused.reason && (
                <div className="text-xs text-red-300/70 mt-0.5">{metrics.messaging_paused.reason}</div>
              )}
            </div>
          </div>
          <Button
            data-testid="reactivate-messaging-btn"
            onClick={reactivateMessaging}
            size="sm"
            className="bg-red-500 hover:bg-red-400 text-red-950 font-semibold shrink-0"
          >
            Reativar envios
          </Button>
        </div>
      )}

      {brokenChecks.length > 0 && (
        <div
          data-testid="health-issue-banner"
          className="flex items-center justify-between gap-4 rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 flex-wrap"
        >
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0" />
            <div className="text-sm text-amber-200">
              {brokenChecks.map(([k, v]) => HEALTH_LABELS[k] || k).join(", ")} com problema:{" "}
              <span className="text-amber-300/80">{brokenChecks[0][1].message}</span>
            </div>
          </div>
          <Link to="/saude">
            <Button size="sm" variant="outline" className="border-amber-500/50 text-amber-200 hover:bg-amber-500/20 shrink-0">
              Ver Saúde do Sistema <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
            </Button>
          </Link>
        </div>
      )}

      {/* ---- Indicadores ---- */}
      <section className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3" data-testid="kpi-grid">
        {!k ? Array.from({ length: 10 }).map((_, i) => <Skeleton key={i} className="h-[118px]" />) : (
          <>
            <Kpi testId="kpi-received" label={`Recebido ${plabel}`} value={brl(k.received.total)} icon={Wallet}
              delta={k.received.delta_pct} sub={`${num(k.received.count)} pagamentos`} spark={k.received.spark} />
            <Kpi testId="kpi-today" label="Recebido hoje" value={brl(k.today.total)} icon={CheckCircle2}
              sub={`${num(k.today.count)} pagamento(s)`} />
            <Kpi testId="kpi-overdue" label="Em atraso" value={brl(k.overdue.total)} icon={TrendingDown}
              accent={k.overdue.count > 0 ? "text-red-400" : "text-emerald-400"} sub={`${num(k.overdue.count)} boletos`} />
            <Kpi testId="kpi-upcoming" label="A vencer 7d" value={brl(k.upcoming7.total)} icon={CalendarClock}
              accent="text-sky-400" sub={`${num(k.upcoming7.count)} boletos`} />
            <Kpi testId="kpi-conversion" label="Conversão" value={`${k.conversion.rate.toLocaleString("pt-BR")}%`} icon={Percent}
              delta={k.conversion.prev_rate != null ? Math.round((k.conversion.rate - k.conversion.prev_rate) * 10) / 10 : null}
              sub={`${num(k.conversion.paid)} de ${num(k.conversion.charged)} pagos`} accent="text-violet-400" />
            <Kpi testId="kpi-ticket" label="Ticket médio" value={brl(k.avg_ticket)} icon={Receipt}
              sub={k.ttp.median_hours != null ? `paga em ${fmtHours(k.ttp.median_hours)} (mediana)` : undefined} accent="text-amber-400" />
            <Kpi testId="kpi-messages" label={`Mensagens ${plabel}`} value={num(k.messages.total)} icon={MessageSquare}
              delta={k.messages.delta_pct} spark={k.messages.spark} sparkColor={COLORS.openpix} accent="text-sky-400" />
            <Kpi testId="kpi-chats" label={`Atend. bot ${plabel}`} value={num(k.chats.total)} icon={Activity}
              delta={k.chats.delta_pct} spark={k.chats.spark} sparkColor={COLORS.violet} accent="text-violet-400" />
            <Kpi testId="kpi-clients" label="Clientes ativos" value={k.clients.active != null ? num(k.clients.active) : "—"} icon={Users}
              sub={k.clients.blocked != null ? `${num(k.clients.blocked)} bloqueados` : undefined} />
            <Kpi testId="kpi-whatsapp" label="WhatsApp" value={metrics?.whatsapp_connected ? "Online" : "Offline"} icon={PlugZap}
              accent={metrics?.whatsapp_connected ? "text-emerald-400" : "text-orange-400"}
              sub={`${num(metrics?.messages_total || 0)} msgs no total`} />
          </>
        )}
      </section>

      {insights.length > 0 && (
        <section className="rounded-xl border border-emerald-500/15 bg-emerald-500/[0.04] px-4 py-3 flex items-center gap-x-6 gap-y-2 flex-wrap" data-testid="insights">
          <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-emerald-400"><Sparkles className="w-3.5 h-3.5" /> Destaques</span>
          {insights.map((it, i) => (
            <span key={i} className="flex items-center gap-1.5 text-xs text-slate-300"><it.icon className="w-3.5 h-3.5 text-slate-500" />{it.text}</span>
          ))}
        </section>
      )}

      {/* ---- Recebimentos ---- */}
      <section className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <ChartCard className="xl:col-span-2" testId="chart-received" title="Recebimentos por dia" subtitle="PIX pagos via Quitaí, por banco, com média móvel de 7 dias"
          right={<LegendChips hidden={hidden} onToggle={toggle} items={[
            { key: "efi", label: "EFI", color: COLORS.efi }, { key: "openpix", label: "OpenPix", color: COLORS.openpix }, { key: "ma7", label: "Média 7d", color: COLORS.amber },
          ]} />}>
          <div className="h-[290px]">
            {!an ? <Skeleton className="h-full" /> : (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={daily} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                  <CartesianGrid stroke={COLORS.grid} vertical={false} />
                  <XAxis dataKey="date" tickFormatter={dm} tick={axis} axisLine={false} tickLine={false} interval="preserveStartEnd" minTickGap={18} />
                  <YAxis tickFormatter={brlCompact} tick={axis} axisLine={false} tickLine={false} width={58} />
                  <Tooltip cursor={{ fill: "rgba(148,163,184,0.06)" }} content={<ChartTooltip labelFormat={(l) => `${dm(l)} · ${weekday(l)}`} valueFormat={(v) => brl(v)} />} />
                  {!hidden.efi && <Bar dataKey="efi" name="EFI" stackId="a" fill={COLORS.efi} radius={[0, 0, 0, 0]} />}
                  {!hidden.openpix && <Bar dataKey="openpix" name="OpenPix" stackId="a" fill={COLORS.openpix} radius={[3, 3, 0, 0]} />}
                  {!hidden.ma7 && <Line type="monotone" dataKey="ma7" name="Média 7d" stroke={COLORS.amber} strokeWidth={2} dot={false} />}
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </div>
        </ChartCard>

        <ChartCard testId="chart-provider" title="Recebido por banco" subtitle={days === "month" ? "Mês atual (do dia 1 até hoje)" : days === "custom" ? `${fmtD(an?.range?.start)} a ${fmtD(an?.range?.end)}` : `Período de ${days} dias`}>
          <div className="h-[290px] relative">
            {!an ? <Skeleton className="h-full" /> : providerTotal <= 0 ? <Empty /> : (
              <>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={an.by_provider} dataKey="value" nameKey="name" innerRadius="62%" outerRadius="86%" paddingAngle={3} stroke="none">
                      {an.by_provider.map((p) => <Cell key={p.key} fill={p.key === "efi" ? COLORS.efi : COLORS.openpix} />)}
                    </Pie>
                    <Tooltip wrapperStyle={{ zIndex: 30 }} content={<ChartTooltip valueFormat={(v) => brl(v)} />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Total</div>
                  <div className="font-mono-jb text-white text-lg tabular-nums">{brlCompact(providerTotal)}</div>
                </div>
              </>
            )}
          </div>
          {an && providerTotal > 0 && (
            <div className="grid grid-cols-2 gap-2 mt-1">
              {an.by_provider.map((p) => (
                <div key={p.key} className="rounded-lg border border-slate-800 px-3 py-2">
                  <div className="flex items-center gap-1.5 text-xs text-slate-400"><span className="w-2 h-2 rounded-full" style={{ background: p.key === "efi" ? COLORS.efi : COLORS.openpix }} />{p.name}</div>
                  <div className="font-mono-jb text-white text-sm tabular-nums whitespace-nowrap">{brl(p.value)}</div>
                  <div className="text-[11px] text-slate-500">{Math.round((p.value / providerTotal) * 100)}%</div>
                </div>
              ))}
            </div>
          )}
        </ChartCard>
      </section>

      {/* ---- Inadimplencia e previsao ---- */}
      <section className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <ChartCard testId="chart-aging" title="Inadimplência por faixa de atraso" subtitle="Boletos vencidos e ainda não pagos (clientes ativos)">
          <div className="h-[260px]">
            {!an ? <Skeleton className="h-full" /> : an.aging.every((a) => !a.count) ? <Empty>Nenhum boleto em atraso 🎉</Empty> : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={an.aging} margin={{ top: 18, right: 8, left: -8, bottom: 0 }}>
                  <CartesianGrid stroke={COLORS.grid} vertical={false} />
                  <XAxis dataKey="label" tick={axis} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={brlCompact} tick={axis} axisLine={false} tickLine={false} width={58} />
                  <Tooltip cursor={{ fill: "rgba(148,163,184,0.06)" }} content={<ChartTooltip hideZero={false} valueFormat={(v, p) => `${brl(v)} · ${p.payload.count} boleto(s)`} />} />
                  <Bar dataKey="total" name="Em atraso" radius={[6, 6, 0, 0]} label={{ position: "top", fill: COLORS.text, fontSize: 11, formatter: (v) => (v ? brlCompact(v) : "") }}>
                    {an.aging.map((_, i) => <Cell key={i} fill={AGING_COLORS[i]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </ChartCard>

        <ChartCard testId="chart-forecast" title="Previsão de recebimento" subtitle="Boletos em aberto que vencem nos próximos 14 dias">
          <div className="h-[260px]">
            {!an ? <Skeleton className="h-full" /> : forecastData.every((f) => !f.count) ? <Empty>Nenhum vencimento nos próximos 14 dias</Empty> : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={forecastData} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                  <CartesianGrid stroke={COLORS.grid} vertical={false} />
                  <XAxis dataKey="label" tick={axis} axisLine={false} tickLine={false} interval="preserveStartEnd" minTickGap={8} />
                  <YAxis tickFormatter={brlCompact} tick={axis} axisLine={false} tickLine={false} width={58} />
                  <Tooltip cursor={{ fill: "rgba(148,163,184,0.06)" }} content={<ChartTooltip labelFormat={(l, p) => `${l} · ${p?.[0]?.payload?.wd || ""}`} valueFormat={(v, p) => `${brl(v)} · ${p.payload.count} boleto(s)`} />} />
                  <Bar dataKey="total" name="A vencer" radius={[5, 5, 0, 0]}>
                    {forecastData.map((f, i) => <Cell key={i} fill={i === 0 ? COLORS.amber : COLORS.openpix} fillOpacity={f.total ? 1 : 0.25} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </ChartCard>
      </section>

      {/* ---- Comportamento de pagamento ---- */}
      <section className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <ChartCard testId="chart-hours" title="Quando os clientes pagam" subtitle="Pagamentos por hora do dia">
          <div className="h-[230px]">
            {!an ? <Skeleton className="h-full" /> : an.hours.every((h) => !h.count) ? <Empty /> : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={an.hours} margin={{ top: 8, right: 4, left: -22, bottom: 0 }}>
                  <CartesianGrid stroke={COLORS.grid} vertical={false} />
                  <XAxis dataKey="h" tickFormatter={(h) => `${h}h`} tick={axis} axisLine={false} tickLine={false} interval={2} />
                  <YAxis tick={axis} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip cursor={{ fill: "rgba(148,163,184,0.06)" }} content={<ChartTooltip hideZero={false} labelFormat={(h) => `${String(h).padStart(2, "0")}:00`} valueFormat={(v, p) => `${v} pgto(s) · ${brl(p.payload.total)}`} />} />
                  <Bar dataKey="count" name="Pagamentos" radius={[3, 3, 0, 0]} fill={COLORS.violet} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </ChartCard>

        <ChartCard testId="chart-ttp" title="Tempo até o pagamento" subtitle={k?.ttp?.avg_hours != null ? `Média ${fmtHours(k.ttp.avg_hours)} · mediana ${fmtHours(k.ttp.median_hours)}` : "Da cobrança enviada ao PIX pago"}>
          <div className="h-[230px]">
            {!an ? <Skeleton className="h-full" /> : an.time_to_pay.every((t) => !t.count) ? <Empty /> : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={an.time_to_pay} margin={{ top: 8, right: 4, left: -22, bottom: 0 }}>
                  <CartesianGrid stroke={COLORS.grid} vertical={false} />
                  <XAxis dataKey="label" axisLine={false} tickLine={false} interval={0} tick={{ ...axis, fontSize: 10 }} />
                  <YAxis tick={axis} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip cursor={{ fill: "rgba(148,163,184,0.06)" }} content={<ChartTooltip hideZero={false} valueFormat={(v) => `${v} pagamento(s)`} />} />
                  <Bar dataKey="count" name="Pagamentos" radius={[5, 5, 0, 0]}>
                    {an.time_to_pay.map((_, i) => <Cell key={i} fill={["#34d399", "#4ade80", "#a3e635", "#fbbf24", "#fb923c", "#f87171"][i]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </ChartCard>

        <ChartCard testId="chart-funnel" title="Conversão das cobranças" subtitle="Boletos cobrados no período e o que aconteceu com eles">
          {!an ? <Skeleton className="h-[230px]" /> : (
            <div className="flex items-center gap-4 h-[230px]">
              <div className="relative w-[46%] h-full shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                  <RadialBarChart innerRadius="72%" outerRadius="100%" data={convRadial} startAngle={90} endAngle={-270}>
                    <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                    <RadialBar dataKey="value" cornerRadius={20} background={{ fill: "#1e293b" }} />
                  </RadialBarChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <div className="font-mono-jb text-white text-2xl tabular-nums">{k.conversion.rate.toLocaleString("pt-BR")}%</div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">pagos</div>
                </div>
              </div>
              <div className="flex-1 min-w-0 space-y-2.5">
                {[
                  ["Cobrados", an.funnel.charged, "#64748b"],
                  ["Pagos", an.funnel.paid, COLORS.efi],
                  ["Aguardando", an.funnel.pending, COLORS.amber],
                  ["Expirados", an.funnel.expired, COLORS.red],
                ].map(([lab, v, c]) => (
                  <div key={lab}>
                    <div className="flex justify-between text-xs"><span className="text-slate-400">{lab}</span><span className="font-mono-jb text-white tabular-nums">{num(v)}</span></div>
                    <div className="h-1.5 rounded-full bg-slate-800 mt-1 overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${an.funnel.charged ? (v / an.funnel.charged) * 100 : 0}%`, background: c }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </ChartCard>
      </section>

      {/* ---- Atividade ---- */}
      <section className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <ChartCard testId="chart-messages" title="Mensagens enviadas por dia" subtitle="Por tipo de envio"
          right={<LegendChips hidden={hidden} onToggle={toggle} items={(an?.msg_groups || []).map((g) => ({ key: g.key, label: g.label, color: MSG_COLORS[g.key] }))} />}>
          <div className="h-[250px]">
            {!an ? <Skeleton className="h-full" /> : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={daily} margin={{ top: 8, right: 8, left: -22, bottom: 0 }}>
                  <CartesianGrid stroke={COLORS.grid} vertical={false} />
                  <XAxis dataKey="date" tickFormatter={dm} tick={axis} axisLine={false} tickLine={false} interval="preserveStartEnd" minTickGap={18} />
                  <YAxis tick={axis} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip cursor={{ fill: "rgba(148,163,184,0.06)" }} content={<ChartTooltip labelFormat={(l) => `${dm(l)} · ${weekday(l)}`} />} />
                  {an.msg_groups.map((g) => !hidden[g.key] && <Bar key={g.key} dataKey={g.key} name={g.label} stackId="m" fill={MSG_COLORS[g.key]} />)}
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </ChartCard>

        <ChartCard testId="chart-chats" title="Atendimentos do bot por dia" subtitle="Mensagens recebidas dos clientes no WhatsApp">
          <div className="h-[250px]">
            {!an ? <Skeleton className="h-full" /> : (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={daily} margin={{ top: 8, right: 8, left: -22, bottom: 0 }}>
                  <defs>
                    <linearGradient id="g-chats" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={COLORS.violet} stopOpacity={0.45} />
                      <stop offset="100%" stopColor={COLORS.violet} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke={COLORS.grid} vertical={false} />
                  <XAxis dataKey="date" tickFormatter={dm} tick={axis} axisLine={false} tickLine={false} interval="preserveStartEnd" minTickGap={18} />
                  <YAxis tick={axis} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip content={<ChartTooltip labelFormat={(l) => `${dm(l)} · ${weekday(l)}`} hideZero={false} />} />
                  <Area type="monotone" dataKey="chats" name="Atendimentos" stroke={COLORS.violet} strokeWidth={2} fill="url(#g-chats)" />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </div>
        </ChartCard>
      </section>

      {/* ---- Evolucao mensal ---- */}
      <ChartCard testId="chart-monthly" title="Evolução mensal" subtitle="Total recebido no mk-auth (todos os meios de pagamento) e clientes novos nos últimos 6 meses"
        right={<LegendChips hidden={hidden} onToggle={toggle} items={[{ key: "rec", label: "Recebido", color: COLORS.efi }, { key: "new", label: "Clientes novos", color: COLORS.rose }]} />}>
        <div className="h-[260px]">
          {!an ? <Skeleton className="h-full" /> : an.monthly.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={an.monthly} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                <CartesianGrid stroke={COLORS.grid} vertical={false} />
                <XAxis dataKey="label" tick={axis} axisLine={false} tickLine={false} />
                <YAxis yAxisId="l" tickFormatter={brlCompact} tick={axis} axisLine={false} tickLine={false} width={58} />
                <YAxis yAxisId="r" orientation="right" tick={axis} axisLine={false} tickLine={false} allowDecimals={false} width={32} />
                <Tooltip cursor={{ fill: "rgba(148,163,184,0.06)" }} content={<ChartTooltip hideZero={false} valueFormat={(v, p) => (p.dataKey === "received" ? brl(v) : num(v))} />} />
                {!hidden.rec && <Bar yAxisId="l" dataKey="received" name="Recebido" fill={COLORS.efi} radius={[6, 6, 0, 0]} maxBarSize={56} />}
                {!hidden.new && <Line yAxisId="r" type="monotone" dataKey="new_clients" name="Clientes novos" stroke={COLORS.rose} strokeWidth={2} dot={{ r: 3 }} />}
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>
      </ChartCard>

    </div>
  );
}
